/**
 * Static collision geometry.
 *
 * Everything in the world that a circle can hit — fluid particles and the ball alike —
 * goes through the same three primitives, so the fluid can never fall through a wall the
 * ball bounces off. All coordinates are world units.
 */

export interface RectCollider {
  type: 'rect';
  /** Centre of the rectangle. */
  x: number;
  y: number;
  /** Full width and height. */
  w: number;
  h: number;
  /** Rotation about the centre, radians. */
  angle?: number;
}

export interface CircleCollider {
  type: 'circle';
  x: number;
  y: number;
  r: number;
}

export interface SegmentCollider {
  type: 'segment';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Full thickness; the segment behaves as a capsule of radius thickness / 2. */
  thickness: number;
}

export type Collider = RectCollider | CircleCollider | SegmentCollider;

/** A contact: unit normal pointing *out* of the collider, plus penetration depth. */
export interface Contact {
  nx: number;
  ny: number;
  depth: number;
}

/** Scratch contact reused by the resolver — never hold on to the returned object. */
const scratch: Contact = { nx: 0, ny: 0, depth: 0 };

/**
 * Resolve a circle against one collider.
 * Returns the shared scratch contact, or null when there is no overlap.
 */
export function collideCircle(
  collider: Collider,
  px: number,
  py: number,
  radius: number,
): Contact | null {
  switch (collider.type) {
    case 'circle':
      return collideCircleCircle(collider, px, py, radius);
    case 'rect':
      return collideCircleRect(collider, px, py, radius);
    case 'segment':
      return collideCircleSegment(collider, px, py, radius);
  }
}

function collideCircleCircle(
  collider: CircleCollider,
  px: number,
  py: number,
  radius: number,
): Contact | null {
  const dx = px - collider.x;
  const dy = py - collider.y;
  const sum = collider.r + radius;
  const distanceSq = dx * dx + dy * dy;
  if (distanceSq >= sum * sum) return null;

  const distance = Math.sqrt(distanceSq);
  if (distance < 1e-6) {
    // Dead centre: no meaningful normal, so pick "up" and push all the way out.
    scratch.nx = 0;
    scratch.ny = -1;
    scratch.depth = sum;
    return scratch;
  }
  scratch.nx = dx / distance;
  scratch.ny = dy / distance;
  scratch.depth = sum - distance;
  return scratch;
}

function collideCircleRect(
  collider: RectCollider,
  px: number,
  py: number,
  radius: number,
): Contact | null {
  const angle = collider.angle ?? 0;
  const cos = angle === 0 ? 1 : Math.cos(angle);
  const sin = angle === 0 ? 0 : Math.sin(angle);

  // Into the rectangle's local frame.
  const rx = px - collider.x;
  const ry = py - collider.y;
  const lx = rx * cos + ry * sin;
  const ly = -rx * sin + ry * cos;

  const hw = collider.w * 0.5;
  const hh = collider.h * 0.5;

  const clampedX = lx < -hw ? -hw : lx > hw ? hw : lx;
  const clampedY = ly < -hh ? -hh : ly > hh ? hh : ly;

  let nx: number;
  let ny: number;
  let depth: number;

  if (clampedX !== lx || clampedY !== ly) {
    // Centre is outside the box: nearest-point test.
    const dx = lx - clampedX;
    const dy = ly - clampedY;
    const distanceSq = dx * dx + dy * dy;
    if (distanceSq >= radius * radius) return null;
    const distance = Math.sqrt(distanceSq);
    if (distance < 1e-6) return null;
    nx = dx / distance;
    ny = dy / distance;
    depth = radius - distance;
  } else {
    // Centre is inside the box: escape through the nearest face.
    const toLeft = lx + hw;
    const toRight = hw - lx;
    const toTop = ly + hh;
    const toBottom = hh - ly;
    const minimum = Math.min(toLeft, toRight, toTop, toBottom);
    if (minimum === toLeft) {
      nx = -1;
      ny = 0;
    } else if (minimum === toRight) {
      nx = 1;
      ny = 0;
    } else if (minimum === toTop) {
      nx = 0;
      ny = -1;
    } else {
      nx = 0;
      ny = 1;
    }
    depth = radius + minimum;
  }

  // Back to world space.
  scratch.nx = nx * cos - ny * sin;
  scratch.ny = nx * sin + ny * cos;
  scratch.depth = depth;
  return scratch;
}

function collideCircleSegment(
  collider: SegmentCollider,
  px: number,
  py: number,
  radius: number,
): Contact | null {
  const ax = collider.x1;
  const ay = collider.y1;
  const bx = collider.x2 - ax;
  const by = collider.y2 - ay;
  const lengthSq = bx * bx + by * by;

  let t = lengthSq > 1e-9 ? ((px - ax) * bx + (py - ay) * by) / lengthSq : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;

  const closestX = ax + bx * t;
  const closestY = ay + by * t;
  const sum = collider.thickness * 0.5 + radius;

  const dx = px - closestX;
  const dy = py - closestY;
  const distanceSq = dx * dx + dy * dy;
  if (distanceSq >= sum * sum) return null;

  const distance = Math.sqrt(distanceSq);
  if (distance < 1e-6) {
    // On the spine: push out perpendicular to the segment.
    const inverse = 1 / Math.sqrt(lengthSq || 1);
    scratch.nx = -by * inverse;
    scratch.ny = bx * inverse;
    scratch.depth = sum;
    return scratch;
  }
  scratch.nx = dx / distance;
  scratch.ny = dy / distance;
  scratch.depth = sum - distance;
  return scratch;
}

/** Axis-aligned bounds of a collider, used for broad-phase culling and rendering. */
export function colliderBounds(collider: Collider): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  switch (collider.type) {
    case 'circle':
      return {
        minX: collider.x - collider.r,
        minY: collider.y - collider.r,
        maxX: collider.x + collider.r,
        maxY: collider.y + collider.r,
      };
    case 'rect': {
      const angle = collider.angle ?? 0;
      const cos = Math.abs(Math.cos(angle));
      const sin = Math.abs(Math.sin(angle));
      const hw = (collider.w * cos + collider.h * sin) * 0.5;
      const hh = (collider.w * sin + collider.h * cos) * 0.5;
      return {
        minX: collider.x - hw,
        minY: collider.y - hh,
        maxX: collider.x + hw,
        maxY: collider.y + hh,
      };
    }
    case 'segment': {
      const half = collider.thickness * 0.5;
      return {
        minX: Math.min(collider.x1, collider.x2) - half,
        minY: Math.min(collider.y1, collider.y2) - half,
        maxX: Math.max(collider.x1, collider.x2) + half,
        maxY: Math.max(collider.y1, collider.y2) + half,
      };
    }
  }
}
